import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import CustomTypography from "../../globalStyles/fonts";
import { categoryService } from "../../utils/services/categoryService";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import { clientService } from "../../utils/services/clientService";
import { fieldSx } from "../../pages/manage-catalog/constants";

const CategoryModal = ({ open, handleClose, onAddData, selectedCategory }) => {
  const clientId = localStorage.getItem("client_id");

  const [details, setDetails] = useState({
    name: "",
    parent: null,
    client: null,
  });
  const [formErrors, setFormErrors] = useState({});
  const [parentOptions, setParentOptions] = useState([]);
  const [parentSearch, setParentSearch] = useState("");
  const [parentLoading, setParentLoading] = useState(false);

  const reset = () => {
    setDetails({ name: "", parent: null, client: null });
    setFormErrors({});
    setParentSearch("");
  };

  const validate = () => {
    const errors = {};
    if (!details.name?.trim()) errors.name = "Category name is required.";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (open) {
      if (selectedCategory) {
        setDetails({
          name: selectedCategory.name || "",
          parent: selectedCategory.parent || null,
          client: selectedCategory.client || null,
        });
      } else {
        reset();
      }
    }
  }, [selectedCategory, open]);

  useEffect(() => {
    if (!open) return;
    const fetchParents = async () => {
      setParentLoading(true);
      try {
        const res = await categoryService.fetchNames(
          parentSearch,
          clientId,
          30,
          0,
        );
        // Exclude current category from parent options (avoid self-reference)
        const filtered = (res?.data || []).filter(
          (c) => c._id !== selectedCategory?._id,
        );
        setParentOptions(filtered);
      } catch (e) {
        console.error(e);
      } finally {
        setParentLoading(false);
      }
    };
    const timer = setTimeout(fetchParents, 300);
    return () => clearTimeout(timer);
  }, [parentSearch, open, selectedCategory]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const payload = {
        name: details.name.trim(),
        parent: details.parent?._id || null,
        client: clientId || details.client?._id,
      };
      if (selectedCategory) {
        await categoryService.edit(payload, selectedCategory._id);
        toast.success("Category updated successfully!");
      } else {
        await categoryService.create(payload);
        toast.success("Category created successfully!");
      }
      onAddData && onAddData();
      reset();
      handleClose();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <CustomTypography fontSize={16} fontWeight={600}>
            {selectedCategory ? "Edit Category" : "Add Category"}
          </CustomTypography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {!clientId && (
          <Grid item xs={12}>
            <AutocompleteWithPagination
              selectedItem={details.client}
              onDataChange={(val) =>
                setDetails((prev) => ({ ...prev, client: val }))
              }
              width="100%"
              optionKey="business_name"
              backgroundColor="var(--bg-paper)"
              customLabel="Client"
              apiEndPoint={(limit, skip, search) =>
                clientService.fetch(limit, skip, search, null, "Active")
              }
            />
          </Grid>
        )}
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12}>
            <TextField
              label="Category Name"
              name="name"
              value={details.name}
              onChange={(e) =>
                setDetails((prev) => ({ ...prev, name: e.target.value }))
              }
              error={!!formErrors.name}
              helperText={formErrors.name}
              fullWidth
              size="small"
              required
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12}>
            <AutocompleteWithPagination
              disabled={!(clientId || details.client)}
              selectedItem={details.parent}
              selectedClient={clientId ? { _id: clientId } : details.client}
              excludeIds={selectedCategory?._id ? [selectedCategory._id] : []}
              onDataChange={(val) =>
                setDetails((prev) => ({ ...prev, parent: val }))
              }
              width="100%"
              optionKey="name"
              backgroundColor="var(--bg-paper)"
              customLabel="Parent Category"
              apiEndPoint={(
                limit,
                skip,
                search,
                clientId,
                supplier,
                excludeIds,
              ) =>
                categoryService.fetchNames(
                  search,
                  clientId,
                  limit,
                  skip,
                  excludeIds,
                )
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave}>
          {selectedCategory ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryModal;
